import React, { useEffect, useState } from 'react';
import {
  Badge,
  NavLink,
  PopoverBody,
  PopoverHeader,
  Table,
  UncontrolledPopover,
} from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import EyeIcon from 'mdi-react/EyeIcon';
import EmailCheckOutlineIcon from 'mdi-react/EmailCheckOutlineIcon';
import EmailOpenOutlineIcon from 'mdi-react/EmailOpenOutlineIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { Link } from 'react-router-dom';
import t from '../../../util/translation/translation';
import Pagination from '../pagination/Pagination';
import Loading from '../custom/Loading';
import Api from '../../../util/api';
import FormCard from '../custom/FormCard';
import SquareIconButton from '../custom/SquareIconButton';
import TooltipWithIcon from '../custom/TooltipWithIcon';
import { formatDateTime } from '../../../util/date-and-time';
import skeletonTableLoader from '../custom/SkeletonLoaders';
import { getMessageType, getOriginLink, getTitleAndBody } from './utils';

// Quick visual read on open rate - push open rates here typically land
// somewhere around 1-10%, not the 20%+ a generic "engagement rate" scale
// would assume, so the thresholds are calibrated to that actual range.
const getOpenRateColor = (percentage) => {
  if (percentage >= 8) return 'success';
  if (percentage >= 3) return 'warning';
  return 'secondary';
};

// Opens trickle in as devices come online, so a rate checked right after
// sending is misleadingly low - hide it behind a "collecting data" badge
// for a while rather than show a number that will keep climbing.
const OPEN_RATE_COLLECTING_MINUTES = 90;
const isCollectingOpenData = (message) => {
  const sentAt = message?.date || message?.createdAt;
  if (!sentAt) return false;
  return (
    moment().diff(moment(sentAt), 'minutes') < OPEN_RATE_COLLECTING_MINUTES
  );
};

const SentMessagesTable = () => {
  const [order, setOrder] = useState({
    page: 1,
    size: 10,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const handleDataReceived = (incommingStatData) => {
    setMessages((previousMessages) => {
      return previousMessages.map((previousMessage) => {
        const existingMessagePushStatIds =
          previousMessage?.pushStats?.map((item) => item.id) || [];
        if (!existingMessagePushStatIds.includes(incommingStatData.id)) {
          return previousMessage;
        }
        const countOfSentTo = previousMessage?.pushStats?.reduce(
          (tot, curr) => tot + curr.successCount,
          0
        );
        const totalLabels = [
          ...(previousMessage?.mappedPushStat?.labels || []),
          ...incommingStatData.labels,
        ];
        const userIdsWatched = totalLabels.flatMap((label) => label.user_ids);
        const amountOfUsers = [...new Set(userIdsWatched)].length;
        return {
          ...previousMessage,
          mappedPushStat: {
            id: incommingStatData.id,
            success_count: countOfSentTo,
            opened_count: amountOfUsers,
            openedPercentage: countOfSentTo
              ? Math.round((amountOfUsers / countOfSentTo) * 100)
              : 0,
            labels: totalLabels,
          },
        };
      });
    });
  };
  const getSendMessages = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const [{ data: scheduledMessagesRes }, resNotificationLabels] =
        await Promise.all([
          Api.scheduleMessages.getScheduleMessages(page, pageSize, 'Sent'),
          Api.userDevices.getAllNotificationLabels(),
        ]);
      const filledData = await Promise.all(
        scheduledMessagesRes.data.map((message) =>
          getOriginLink(message)
            .then((originalLinkResult) => ({
              ...message,
              originalLinkResult,
              notificationLabelTitle:
                resNotificationLabels.find(
                  (label) => label.id === message?.payload?.notificationLabel
                )?.title || null,
            }))
            .catch(() => ({
              ...message,
              originalLinkResult: { title: null, link: null },
            }))
        )
      );
      const pushStatIds = scheduledMessagesRes.data
        .flatMap((message) =>
          message?.pushStats?.map((pushStat) => pushStat.id)
        )
        .filter((item) => !!item);
      setMessages(filledData);

      setLoading(false);
      setTotal(scheduledMessagesRes.total);
      await Api.scheduleMessages.getStatsForMessages(
        pushStatIds,
        handleDataReceived
      );
    } catch (err) {
      toast.error(err || 'error');
      setLoading(false);
    }
  };

  const onChangePageSystem = (pager) => {
    if (order.page !== pager.currentPage || order.size !== pager.pageSize) {
      getSendMessages(pager.currentPage, pager.pageSize);
      setOrder({
        page: pager.currentPage,
        size: pager.pageSize,
      });
    }
  };
  useEffect(() => {
    getSendMessages();
  }, []);
  const renderSentMessages = () => {
    return messages.map((message) => {
      let typeColor;
      const messageType = getMessageType(message);
      switch (messageType) {
        case 'push':
          typeColor = 'success';
          break;
        case 'sms':
          typeColor = 'premium';
          break;
        case 'email':
          typeColor = 'info';
          break;
        default:
          typeColor = 'default';
      }
      const { title, link } = message.originalLinkResult;
      const { messageTitle, messageBody } = getTitleAndBody(message);
      return (
        <tr key={`renderScheduledMessages-${message.id}`}>
          <td>{messageTitle}</td>
          <td>
            <Badge color={typeColor}>{messageType}</Badge>
          </td>
          <td>
            {message?.date
              ? formatDateTime(message?.date)
              : message?.createdAt
                ? formatDateTime(message?.createdAt)
                : '-- --'}
          </td>
          <td>
            {message?.pushStats?.length ? (
              message?.mappedPushStat?.success_count === undefined ||
              message?.mappedPushStat?.openedPercentage === undefined ? (
                skeletonTableLoader(1, 1, 50)
              ) : (
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                  <div
                    className="d-flex align-items-center text-muted"
                    style={{ gap: 4 }}
                  >
                    <EmailCheckOutlineIcon size={14} />
                    <span style={{ fontWeight: 600 }}>
                      {message?.mappedPushStat?.success_count}
                    </span>
                  </div>
                  <div
                    style={{
                      width: 1,
                      height: 18,
                      background: '#e5e7eb',
                    }}
                  />
                  {isCollectingOpenData(message) ? (
                    <Badge pill color="secondary">
                      <EmailOpenOutlineIcon size={14} />{' '}
                      {t('InsightPage.collectingData')}
                    </Badge>
                  ) : (
                    <Link
                      to={{
                        pathname: `/messages/insight/${message.id}`,
                        state: message,
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Badge
                        pill
                        color={getOpenRateColor(
                          message?.mappedPushStat?.openedPercentage
                        )}
                      >
                        <EmailOpenOutlineIcon size={14} />{' '}
                        {`${message?.mappedPushStat?.openedPercentage}%`}{' '}
                        <ChevronRightIcon size={12} />
                      </Badge>
                    </Link>
                  )}
                </div>
              )
            ) : (
              (getMessageType(message) === 'sms' ||
                getMessageType(message) === 'email') &&
              message?.payload?.recipients?.length !== undefined && (
                <div
                  className="d-flex align-items-center text-muted"
                  style={{ gap: 4 }}
                >
                  <EmailCheckOutlineIcon size={14} />
                  <span style={{ fontWeight: 600 }}>
                    {message?.payload?.recipients?.length}
                  </span>
                </div>
              )
            )}
          </td>
          <td>
            {message?.notificationLabelTitle || ''}
            {/*getMessageType(message) === 'sms' &&
            message?.payload?.targetAudience?.type
            */}
          </td>
          <td>
            {message?.externalSender?.entityName && (
              <NavLink href="false" disabled="true">
                {message?.externalSender?.entityName}
              </NavLink>
            )}
            {!message?.externalSender?.entityName && (
              <NavLink href={link} disabled={!link}>
                {title}
              </NavLink>
            )}
          </td>
          <td>
            <div className="icon-actions">
              <SquareIconButton
                icon={<EyeIcon />}
                onClick={() => {}}
                id={`messageBody${message.id}`}
              />
            </div>
            <UncontrolledPopover
              trigger="legacy"
              placement="left"
              target={`messageBody${message.id}`}
              style={{ maxWidth: 420 }}
            >
              <PopoverHeader style={{ padding: '12px 16px', fontSize: 16 }}>
                <span style={{ wordBreak: 'break-word' }}>{messageTitle}</span>
              </PopoverHeader>
              <PopoverBody style={{ padding: 16 }}>
                <small className="text-muted d-block mb-2">
                  {t('shared.description')}
                </small>
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  {messageBody || '-'}
                </div>
              </PopoverBody>
            </UncontrolledPopover>
          </td>
        </tr>
      );
    });
  };
  return (
    <div className="flex-1">
      <Loading loading={loading} />
      <FormCard
        title={t('ScheduledMessagesPage.sentMessages')}
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy">
          <thead>
            <tr>
              <th>{t('shared.title')}</th>
              <th>{t('shared.type')}</th>
              <th>{t('shared.publication')}</th>
              <th>
                {t('InsightPage.engagement')}{' '}
                <TooltipWithIcon
                  id="sent-messages-engagement-tooltip"
                  text={t('InsightPage.engagementTooltip')}
                  size={14}
                />
              </th>
              <th>{t('MessagesPage.notificationLabel')}</th>
              <th>{t('shared.origin')}</th>
              <th>{t('shared.message')}</th>
            </tr>
          </thead>
          <tbody>{renderSentMessages()}</tbody>
        </Table>
      </FormCard>
      <Pagination
        items={total}
        onChangePage={onChangePageSystem}
        initialPage={0}
      />
    </div>
  );
};

export default SentMessagesTable;
